import type { EventSkeleton, Message } from '../types/sessions.js';

export interface SeqRange {
  lo: number;
  hi: number;
}

interface Block extends SeqRange {
  size: number;
}

export function deriveHistoryWindowCeiling(deviceMemoryGiB?: number): number {
  const giB =
    deviceMemoryGiB ??
    (typeof navigator !== 'undefined' ? (navigator as { deviceMemory?: number }).deviceMemory : undefined) ??
    4;
  const budgetBytes = giB * 16 * 1024 * 1024;
  const perRowBytes = 2 * 1024;
  return Math.max(2000, Math.floor(budgetBytes / perRowBytes));
}

export function eventTailFloor(events: readonly EventSkeleton[]): number {
  for (let i = events.length - 1; i >= 0; i--) {
    if (events[i].event_type === 'reasoning_started') return events[i].sequence_num;
  }
  return events.length > 0 ? events[0].sequence_num : 0;
}

function eventBlocks(events: readonly EventSkeleton[], below: number): Block[] {
  const blocks: Block[] = [];
  let current: Block | null = null;
  for (const e of events) {
    if (e.sequence_num >= below) break;
    const startsRun = e.event_type === 'reasoning_started';
    if (current === null || startsRun || e.sequence_num !== current.hi + 1) {
      current = { lo: e.sequence_num, hi: e.sequence_num, size: 1 };
      blocks.push(current);
    } else {
      current.hi = e.sequence_num;
      current.size++;
    }
  }
  return blocks;
}

function messageBlocks(messages: readonly Message[], below: number): Block[] {
  return messages
    .filter((m) => m.message_index < below)
    .map((m) => ({ lo: m.message_index, hi: m.message_index, size: 1 }));
}

function trimFarthest(blocks: Block[], budget: number, center: number): Block[] {
  const kept = [...blocks];
  let count = kept.reduce((n, b) => n + b.size, 0);
  while (count > budget && kept.length > 0) {
    const first = kept[0];
    const last = kept[kept.length - 1];
    const dropFirst = Math.abs(center - first.lo) >= Math.abs(last.hi - center);
    const dropped = dropFirst ? kept.shift()! : kept.pop()!;
    count -= dropped.size;
  }
  return kept;
}

function trimFromEnd(blocks: Block[], budget: number, from: 'oldest' | 'newest'): Block[] {
  const kept = [...blocks];
  let count = kept.reduce((n, b) => n + b.size, 0);
  while (count > budget && kept.length > 0) {
    const dropped = from === 'oldest' ? kept.shift()! : kept.pop()!;
    count -= dropped.size;
  }
  return kept;
}

function select<T>(rows: readonly T[], key: (row: T) => number, floor: number, kept: Block[]): T[] {
  return rows.filter((row) => {
    const k = key(row);
    return k >= floor || kept.some((b) => k >= b.lo && k <= b.hi);
  });
}

// (sdk ADR-f43c7)
export function retainByDemand<T>(
  rows: readonly T[],
  key: (row: T) => number,
  blocks: Block[],
  floor: number,
  demands: readonly SeqRange[],
  margin: number,
  ceiling: number,
): T[] {
  if (demands.length === 0) return select(rows, key, floor, []);
  const hull = {
    lo: Math.min(...demands.map((d) => d.lo)) - margin,
    hi: Math.max(...demands.map((d) => d.hi)) + margin,
  };
  const wanted = blocks.filter((b) => b.hi >= hull.lo && b.lo <= hull.hi);
  const tailCount = rows.filter((row) => key(row) >= floor).length;
  const kept = trimFarthest(wanted, Math.max(0, ceiling - tailCount), demands[0].lo);
  return select(rows, key, floor, kept);
}

export function retainUnderCeiling<T>(
  rows: readonly T[],
  key: (row: T) => number,
  blocks: Block[],
  floor: number,
  ceiling: number,
  dropFrom: 'oldest' | 'newest',
): T[] {
  if (rows.length <= ceiling) return [...rows];
  const tailCount = rows.filter((row) => key(row) >= floor).length;
  return select(rows, key, floor, trimFromEnd(blocks, Math.max(0, ceiling - tailCount), dropFrom));
}

export function retainEvents(
  events: readonly EventSkeleton[],
  demands: readonly SeqRange[],
  margin: number,
  ceiling: number,
): EventSkeleton[] {
  const floor = eventTailFloor(events);
  return retainByDemand(events, (e) => e.sequence_num, eventBlocks(events, floor), floor, demands, margin, ceiling);
}

export function capEvents(
  events: readonly EventSkeleton[],
  ceiling: number,
  dropFrom: 'oldest' | 'newest',
): EventSkeleton[] {
  const floor = eventTailFloor(events);
  return retainUnderCeiling(events, (e) => e.sequence_num, eventBlocks(events, floor), floor, ceiling, dropFrom);
}

export function retainMessages(
  messages: readonly Message[],
  floor: number,
  demand: SeqRange | null,
  margin: number,
  ceiling: number,
): Message[] {
  return retainByDemand(
    messages,
    (m) => m.message_index,
    messageBlocks(messages, floor),
    floor,
    demand ? [demand] : [],
    margin,
    ceiling,
  );
}

export function capMessages(
  messages: readonly Message[],
  floor: number,
  ceiling: number,
  dropFrom: 'oldest' | 'newest',
): Message[] {
  return retainUnderCeiling(
    messages,
    (m) => m.message_index,
    messageBlocks(messages, floor),
    floor,
    ceiling,
    dropFrom,
  );
}

export function firstGap<T>(rows: readonly T[], key: (row: T) => number, through: number): SeqRange | null {
  for (let i = 0; i + 1 < rows.length; i++) {
    const lo = key(rows[i]);
    const hi = key(rows[i + 1]);
    if (hi > through) return null;
    if (hi > lo + 1) return { lo, hi };
  }
  return null;
}
